/**
 * COMPONENT TREE panel for the V5 Aircraft Design mode.
 *
 * Renders the design's geoms (from the last ops response) as an indented
 * parent/child list with per-row visibility, inline rename and a two-click
 * delete, plus an add-component form for the five creatable VSP geom types.
 * Purely presentational - every edit is reported upward as a design op;
 * AircraftDesignPanel owns the request chain and the authoritative state.
 * Styles follow the AircraftSurface idiom - inline CSSProperties off the V5
 * tokens, no hardcoded hexes.
 */

import { useState, type CSSProperties } from "react";

import type {
  AircraftComponent,
  AircraftDesignGeom,
  AircraftDesignGeomType,
} from "../../api/aircraft";

/** The geom types the design workbench can create, in menu order. */
const CREATABLE: { type: AircraftDesignGeomType; label: string }[] = [
  { type: "WING", label: "Wing" },
  { type: "FUSELAGE", label: "Fuselage" },
  { type: "POD", label: "Pod" },
  { type: "STACK", label: "Stack" },
  { type: "BODYOFREVOLUTION", label: "Body of revolution" },
];

/** DFS order with depths - children indented under parents. Geoms whose parent
 *  is missing render as roots; anything unreachable (shouldn't happen, VSP
 *  trees are acyclic) is appended flat so no component can silently vanish. */
export function treeOrder(
  geoms: AircraftDesignGeom[],
): { geom: AircraftDesignGeom; depth: number }[] {
  const ids = new Set(geoms.map((g) => g.id));
  const children = new Map<string, AircraftDesignGeom[]>();
  const roots: AircraftDesignGeom[] = [];
  for (const g of geoms) {
    if (g.parent && g.parent !== g.id && ids.has(g.parent)) {
      const list = children.get(g.parent);
      if (list) list.push(g);
      else children.set(g.parent, [g]);
    } else {
      roots.push(g);
    }
  }
  const out: { geom: AircraftDesignGeom; depth: number }[] = [];
  const walk = (list: AircraftDesignGeom[], depth: number) => {
    for (const g of list) {
      out.push({ geom: g, depth });
      walk(children.get(g.id) ?? [], depth + 1);
    }
  };
  walk(roots, 0);
  if (out.length < geoms.length) {
    const seen = new Set(out.map((e) => e.geom.id));
    for (const g of geoms) if (!seen.has(g.id)) out.push({ geom: g, depth: 0 });
  }
  return out;
}

export function AircraftComponentTree({
  geoms,
  components,
  visible,
  selected,
  busy,
  onToggle,
  onSelect,
  onAdd,
  onRename,
  onDelete,
}: {
  /** The design's geoms from the last ops response (id/name/type/parent). */
  geoms: AircraftDesignGeom[];
  /** The rendered bundle's components - patch counts, matched by name. */
  components: AircraftComponent[];
  /** Component name -> shown (absent = shown), same semantics as Browse. */
  visible: Record<string, boolean>;
  selected: string | null;
  /** True while an ops request is in flight - edits stay visible, inputs don't fire. */
  busy: boolean;
  onToggle: (name: string) => void;
  onSelect: (name: string | null) => void;
  onAdd: (type: AircraftDesignGeomType, parent: string | null) => void;
  onRename: (id: string, name: string) => void;
  onDelete: (id: string) => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [addType, setAddType] = useState<AircraftDesignGeomType>("WING");
  const [addParent, setAddParent] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [confirmId, setConfirmId] = useState<string | null>(null);

  const rows = treeOrder(geoms);
  const patchesByName = new Map(components.map((c) => [c.name, c.patches]));
  // A previously chosen parent may have been deleted since - fall back to top level.
  const parentId = geoms.some((g) => g.id === addParent) ? addParent : "";

  const commitRename = () => {
    if (!editingId) return;
    const name = editName.trim();
    const prev = geoms.find((g) => g.id === editingId)?.name;
    setEditingId(null);
    if (name && name !== prev) onRename(editingId, name);
  };

  return (
    <>
      <div style={S.head}>
        <span style={S.headTitle}>Components</span>
        <button style={S.miniBtn} onClick={() => setAddOpen((o) => !o)} disabled={busy}>
          {addOpen ? "Close" : "+ Add"}
        </button>
      </div>
      {addOpen && (
        <div style={S.addForm}>
          <select
            style={S.select}
            value={addType}
            onChange={(e) => setAddType(e.target.value as AircraftDesignGeomType)}
            aria-label="Component type"
          >
            {CREATABLE.map((c) => <option key={c.type} value={c.type}>{c.label}</option>)}
          </select>
          <select
            style={S.select}
            value={parentId}
            onChange={(e) => setAddParent(e.target.value)}
            aria-label="Parent component"
          >
            <option value="">Top level</option>
            {geoms.map((g) => <option key={g.id} value={g.id}>under {g.name}</option>)}
          </select>
          <button
            style={S.miniBtnPrimary}
            disabled={busy}
            onClick={() => {
              onAdd(addType, parentId || null);
              setAddOpen(false);
              setAddParent("");
            }}
          >
            Add
          </button>
        </div>
      )}
      {rows.length === 0 && (
        <div style={S.emptyTree}>
          {busy
            ? "Loading components…"
            : "No components yet - add a wing or fuselage to start."}
        </div>
      )}
      {rows.map(({ geom, depth }) => {
        const isEditing = editingId === geom.id;
        const isConfirming = confirmId === geom.id;
        const patches = patchesByName.get(geom.name);
        return (
          <div
            key={geom.id}
            style={{
              ...S.row,
              paddingLeft: 12 + depth * 14,
              ...(selected === geom.name ? S.rowSel : null),
            }}
            onClick={() => onSelect(selected === geom.name ? null : geom.name)}
          >
            <input
              type="checkbox"
              checked={visible[geom.name] !== false}
              onChange={(e) => { e.stopPropagation(); onToggle(geom.name); }}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Show ${geom.name}`}
            />
            {isEditing ? (
              <input
                style={S.renameInput}
                value={editName}
                autoFocus
                onChange={(e) => setEditName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitRename();
                  if (e.key === "Escape") setEditingId(null);
                }}
                aria-label={`Rename ${geom.name}`}
              />
            ) : (
              <span style={S.rowName} title={geom.id}>{geom.name}</span>
            )}
            <span style={S.rowMeta}>
              {geom.type.toLowerCase()}
              {patches !== undefined ? ` · ${patches} pat` : ""}
            </span>
            {isConfirming ? (
              <span style={S.actions} onClick={(e) => e.stopPropagation()}>
                <button
                  style={S.dangerBtn}
                  disabled={busy}
                  onClick={() => { setConfirmId(null); onDelete(geom.id); }}
                >
                  Delete
                </button>
                <button style={S.miniBtn} onClick={() => setConfirmId(null)}>Keep</button>
              </span>
            ) : (
              <span style={S.actions} onClick={(e) => e.stopPropagation()}>
                <button
                  style={S.iconBtn}
                  disabled={busy}
                  title={`Rename ${geom.name}`}
                  onClick={() => {
                    setEditingId(geom.id);
                    setEditName(geom.name);
                    setConfirmId(null);
                  }}
                >
                  ✎
                </button>
                <button
                  style={S.iconBtn}
                  disabled={busy}
                  title={`Delete ${geom.name}`}
                  onClick={() => { setConfirmId(geom.id); setEditingId(null); }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
        );
      })}
    </>
  );
}

const S: Record<string, CSSProperties> = {
  head: { display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", fontWeight: 600,
    borderBottom: "1px solid var(--v5-line)", fontSize: 12, textTransform: "uppercase",
    letterSpacing: 0.5, opacity: 0.85 },
  headTitle: { flex: 1, opacity: 0.8 },
  miniBtn: { background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "1px 8px", cursor: "pointer", font: "11px var(--v5-sans, system-ui)" },
  miniBtnPrimary: { background: "var(--v5-primary)", color: "#fff", border: "none",
    borderRadius: "var(--v5-radius, 4px)", padding: "2px 10px", cursor: "pointer",
    font: "11px var(--v5-sans, system-ui)" },
  dangerBtn: { background: "var(--v5-surface)", color: "var(--v5-amber, #c80)",
    border: "1px solid var(--v5-amber, #c80)", borderRadius: "var(--v5-radius, 4px)",
    padding: "1px 8px", cursor: "pointer", font: "11px var(--v5-sans, system-ui)" },
  addForm: { display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 12px",
    borderBottom: "1px solid var(--v5-line)" },
  select: { background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "3px 6px", font: "12px var(--v5-sans, system-ui)", minWidth: 0, flex: 1 },
  emptyTree: { padding: 12, fontSize: 12, opacity: 0.6, lineHeight: 1.5 },
  row: { display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", cursor: "pointer",
    borderBottom: "1px solid var(--v5-line)" },
  rowSel: { background: "var(--v5-primary-soft, rgba(80,120,180,0.15))" },
  rowName: { flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  renameInput: { flex: 1, minWidth: 0, background: "var(--v5-surface)", color: "var(--v5-ink)",
    border: "1px solid var(--v5-line)", borderRadius: "var(--v5-radius, 4px)",
    padding: "1px 4px", font: "12px var(--v5-sans, system-ui)" },
  rowMeta: { font: "11px var(--v5-mono, monospace)", opacity: 0.6, whiteSpace: "nowrap" },
  actions: { display: "flex", alignItems: "center", gap: 4 },
  iconBtn: { background: "transparent", color: "var(--v5-ink)", border: "none",
    cursor: "pointer", font: "12px var(--v5-sans, system-ui)", opacity: 0.65,
    padding: "0 2px", lineHeight: 1 },
};
